import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import SearchBar from './SearchBar';
import { useIsMobile } from '../hooks/useIsMobile';
import { trackAnalyticsEvent } from '../services/analytics';

const RESOURCE_CATEGORIES = [
  { label: 'Alcohol Addiction',   slug: 'alcohol-addiction',            icon: '🍷' },
  { label: 'Opioid & Opiate',     slug: 'opioid-dependency',            icon: '💊' },
  { label: 'Recovery Resources',  slug: 'general',                      icon: '🌱' },
  { label: 'Substance Treatment', slug: 'substance-specific-treatment', icon: '🔬' },
];

const Header: React.FC = () => {
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [ownerOpen, setOwnerOpen]         = useState(false);
  const [drawerOpen, setDrawerOpen]       = useState(false);
  const isMobile     = useIsMobile(960);
  const location     = useLocation();
  const resourcesRef = useRef<HTMLDivElement>(null);
  const ownerRef     = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (resourcesRef.current && !resourcesRef.current.contains(e.target as Node)) setResourcesOpen(false);
      if (ownerRef.current     && !ownerRef.current.contains(e.target as Node))     setOwnerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close drawer on navigation
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  const closeAll = () => { setResourcesOpen(false); setOwnerOpen(false); };

  return (
    <>
      <header className="site-header">
        <div className="header-inner">

          {/* ── Logo ── */}
          <Link to="/" className="header-logo" onClick={closeAll}>
            <span className="logo-icon">⭐</span>
            <div className="logo-text-block">
              <span className="logo-name">TopRatedRehabs</span>
              {!isMobile && <span className="logo-tagline">Find Recovery Now</span>}
            </div>
          </Link>

          {/* ── Search (desktop) ── */}
          {!isMobile && (
            <div className="header-search">
              <SearchBar />
            </div>
          )}

          {/* ── Desktop nav ── */}
          {!isMobile && (
            <nav className="header-nav">
              <NavLink to="/search" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>Browse</NavLink>
              <a href="/rehabs" className="nav-link">By State</a>

              {/* Resources dropdown */}
              <div className="nav-dropdown" ref={resourcesRef}>
                <button
                  className={`nav-link${resourcesOpen ? ' active' : ''}`}
                  onClick={() => { setResourcesOpen(v => !v); setOwnerOpen(false); }}
                >
                  Resources <span className={`caret${resourcesOpen ? ' up' : ''}`}>▾</span>
                </button>
                <div className={`dropdown-panel${resourcesOpen ? ' open' : ''}`}>
                  <Link to="/resources/" className="drop-header" onClick={() => setResourcesOpen(false)}>
                    📚 All Resources
                  </Link>
                  {RESOURCE_CATEGORIES.map(c => (
                    <Link key={c.slug} to={`/resources/c/${c.slug}`} className="drop-item" onClick={() => setResourcesOpen(false)}>
                      <span>{c.icon}</span>{c.label}
                    </Link>
                  ))}
                </div>
              </div>

              <NavLink to="/compare"   className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>Compare</NavLink>
              <NavLink to="/assistant" className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}>AI Helper</NavLink>

              {/* For Owners dropdown */}
              <div className="nav-dropdown" ref={ownerRef}>
                <button
                  className={`nav-cta-btn${ownerOpen ? ' open' : ''}`}
                  onClick={() => { setOwnerOpen(v => !v); setResourcesOpen(false); }}
                >
                  For Owners <span className={`caret${ownerOpen ? ' up' : ''}`}>▾</span>
                </button>
                <div className={`dropdown-panel right${ownerOpen ? ' open' : ''}`}>
                  <Link to="/advertise"   className="drop-header" onClick={() => setOwnerOpen(false)}>📢 Advertise With Us</Link>
                  <Link to="/add-listing" className="drop-item"   onClick={() => setOwnerOpen(false)}>➕ Add Your Facility</Link>
                  <Link to="/search"      className="drop-item"   onClick={() => setOwnerOpen(false)}>🔍 Claim Your Listing</Link>
                  <div className="drop-divider" />
                  <a href="https://admin.topratedrehabs.com" className="drop-item" onClick={() => setOwnerOpen(false)}>🔐 Owner Login</a>
                </div>
              </div>
            </nav>
          )}

          {/* ── Mobile hamburger ── */}
          {isMobile && (
            <>
              {/* Compact search pill for mobile */}
              <Link
                to="/search"
                style={{
                  marginLeft: 'auto',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '7px 12px',
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: '20px',
                  color: 'rgba(255,255,255,0.8)',
                  textDecoration: 'none',
                  fontSize: '13px',
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                }}
              >
                🔍 Search
              </Link>
              <button
                className={`hamburger${drawerOpen ? ' open' : ''}`}
                onClick={() => setDrawerOpen(v => !v)}
                aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
              >
                <span /><span /><span />
              </button>
            </>
          )}
        </div>
      </header>

      {/* ── Mobile side drawer ── */}
      {isMobile && (
        <>
          <div
            className={`nav-drawer-overlay${drawerOpen ? ' open' : ''}`}
            onClick={() => setDrawerOpen(false)}
          />
          <div className={`nav-drawer${drawerOpen ? ' open' : ''}`}>
            {/* Drawer header */}
            <div className="drawer-header">
              <Link to="/" className="drawer-logo" onClick={() => setDrawerOpen(false)}>
                ⭐ TopRatedRehabs
              </Link>
              <button className="drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close">✕</button>
            </div>

            {/* Search in drawer */}
            <div className="drawer-search">
              <SearchBar />
            </div>

            <div className="drawer-scroll">
              <div className="drawer-section">
                <div className="drawer-section-label">Discover</div>
                <Link to="/search"    className="drawer-link" onClick={() => setDrawerOpen(false)}>🏥 Browse Facilities</Link>
                <a href="/rehabs" className="drawer-link">🗺 Browse by State</a>
                <Link to="/compare"   className="drawer-link" onClick={() => setDrawerOpen(false)}>⚖️ Compare Facilities</Link>
                <Link to="/assistant" className="drawer-link" onClick={() => setDrawerOpen(false)}>🤖 AI Helper</Link>
              </div>

              <div className="drawer-section">
                <div className="drawer-section-label">Resources</div>
                <Link to="/resources/" className="drawer-link" onClick={() => setDrawerOpen(false)}>📚 All Resources</Link>
                {RESOURCE_CATEGORIES.map(c => (
                  <Link key={c.slug} to={`/resources/c/${c.slug}`} className="drawer-link drawer-link--sub" onClick={() => setDrawerOpen(false)}>
                    {c.icon} {c.label}
                  </Link>
                ))}
              </div>

              <div className="drawer-section">
                <div className="drawer-section-label">For Treatment Centers</div>
                <Link to="/advertise"   className="drawer-link" onClick={() => setDrawerOpen(false)}>📢 Advertise With Us</Link>
                <Link to="/add-listing" className="drawer-link" onClick={() => setDrawerOpen(false)}>➕ Add Your Facility</Link>
                <Link to="/search"      className="drawer-link" onClick={() => setDrawerOpen(false)}>🔍 Claim Your Listing</Link>
                <a href="https://admin.topratedrehabs.com" className="drawer-link" onClick={() => setDrawerOpen(false)}>🔐 Owner Login</a>
              </div>
            </div>

            {/* Helpline at bottom */}
            <div className="drawer-helpline">
              <div className="drawer-helpline-label">Need Help Now?</div>
              <a href="tel:+18006624357" className="drawer-helpline-number"
                onClick={() => trackAnalyticsEvent('helpline_click', { placement: 'mobile_drawer' })}>1-800-662-4357</a>
              <div className="drawer-helpline-note">Free · Confidential · 24/7</div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;
